"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import Link from "next/link"
import { motion } from "framer-motion"
import { Sword, Download, Users, ChevronRight } from "lucide-react"
import { ParticlesBackground } from "@/components/ui/ParticlesBackground"

export function Hero() {
    const [playerCount, setPlayerCount] = useState(0)

    useEffect(() => {
        fetch("/api/stats")
            .then((r) => r.json())
            .then((d) => setPlayerCount(d.totalAccounts || 0))
            .catch(() => { })
    }, [])

    return (
        <div className="relative min-h-[90vh] w-full overflow-hidden flex items-center">
            {/* Background Layer */}
            <div className="absolute inset-0 z-0 bg-transparent">
                <ParticlesBackground />
                {/* Deep atmospheric overlays (Reduced opacity for new image) */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-950/80 pointer-events-none" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-amber-600/10 via-transparent to-transparent opacity-40 pointer-events-none" />
            </div>

            {/* Glowing Orbs for extra depth */}
            <div className="absolute top-1/3 -left-20 w-96 h-96 bg-amber-600/10 rounded-full blur-[120px] animate-pulse" />
            <div className="absolute bottom-1/3 -right-20 w-96 h-96 bg-purple-600/10 rounded-full blur-[120px] animate-pulse delay-700" />

            {/* Content Container */}
            <div className="relative z-10 container mx-auto px-4 md:px-6">
                <div className="max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        {/* Status Badge */}
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-widest mb-6 backdrop-blur-md">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                            </span>
                            Server Live: Season 3
                        </div>

                        <h1 className="text-6xl md:text-8xl font-black tracking-tighter mb-6 leading-none font-cinzel">
                            <span className="block text-white drop-shadow-2xl">THE DUAL REALM</span>
                            <span className="block bg-gradient-to-r from-amber-300 via-amber-500 to-amber-600 bg-clip-text text-transparent text-glow">
                                EXPERIENCE
                            </span>
                        </h1>

                        <p className="text-xl md:text-2xl text-slate-300 font-medium max-w-2xl mb-10 leading-relaxed font-sans">
                            Choose your destiny in <span className="text-amber-400 font-bold">DragonL2</span>.
                            Whether you seek massive PvP battles on Silverwing or official-like progression on Rhaegal, your epic adventure awaits.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-5">
                            <Link href="/register">
                                <Button size="lg" className="h-16 px-8 text-lg font-bold font-cinzel bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white border-none transition-all rounded-sm shadow-lg shadow-amber-600/20 hover:shadow-amber-500/40 group relative overflow-hidden">
                                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 pointer-events-none" />
                                    <Sword className="mr-2 h-5 w-5 group-hover:rotate-12 transition-transform" />
                                    START YOUR JOURNEY
                                </Button>
                            </Link>
                            <Link href="/download">
                                <Button variant="outline" size="lg" className="h-16 px-8 text-lg font-bold font-cinzel bg-slate-900/40 backdrop-blur-sm border-amber-900/50 text-amber-100 hover:bg-slate-900/60 hover:border-amber-500/50 hover:text-white transition-all rounded-sm group">
                                    <Download className="mr-2 h-5 w-5 group-hover:-translate-y-1 transition-transform text-amber-500" />
                                    GET THE CLIENT
                                </Button>
                            </Link>
                        </div>

                        {/* Quick Stats Preview */}
                        <div className="mt-16 flex items-center gap-12 border-l-2 border-amber-900/30 pl-8">
                            <div>
                                <div className="text-4xl font-black text-white italic font-cinzel">{playerCount.toLocaleString()}+</div>
                                <div className="text-xs font-bold text-amber-500 uppercase tracking-widest font-sans">Registered</div>
                            </div>
                            <div>
                                <div className="text-4xl font-black text-white italic font-cinzel">x1000</div>
                                <div className="text-xs font-bold text-amber-500 uppercase tracking-widest font-sans">EXP Rates</div>
                            </div>
                            <div>
                                <div className="text-4xl font-black text-white italic font-cinzel">24/7</div>
                                <div className="text-xs font-bold text-amber-500 uppercase tracking-widest font-sans">Uptime Guard</div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Bottom Scroll Indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 1 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
            >
                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-500/70 font-bold font-cinzel">Discover More</div>
                <div className="w-px h-16 bg-gradient-to-b from-amber-500 to-transparent opacity-50" />
            </motion.div>
        </div>
    )
}
